<template>
	<div class="content-wrapper">
		<!-- Content -->

		<div class="container flex-grow-1 container-p-y">
			<div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-6 row-gap-4">
				<div class="d-flex flex-column justify-content-center">
					<h4 class="mb-1">{{ $t('invoices.title') }}</h4>
					<p class="mb-0">{{ $t('invoices.description') }}</p>
				</div>
				<div class="d-flex align-content-center flex-wrap gap-4">
					<button class="btn btn-primary waves-effect waves-light" type="button" disabled="" v-if="isDownloading">
						<span class="spinner-border me-1" role="status" aria-hidden="true"></span>
						{{ $t('common.loadingInProgress') }}
					</button>
					<button type="submit" class="btn btn-primary" @click="downloadAllInvoices()" v-if="!isDownloading">{{ $t('invoices.downloadUnpaid') }}</button>
				</div>
			</div>

			<div class="spinner-border spinner-border-lg text-primary load-center" role="status" v-if="isLoading">
				<span class="visually-hidden">{{ $t('common.loading') }}</span>
			</div>

			<div class="alert alert-warning mb-4" v-for="error in sourceErrors" :key="error.code">
				{{ error.message }}
			</div>

			<div class="col-lg-12" v-if="!isLoading">
				<div class="card h-100">
					<div class="table-responsive">
						<table class="table table-borderless">
							<thead>
								<tr class="border-bottom">
									<th class="bg-transparent fs-xsmall">{{ $t('invoices.number') }}</th>
									<th class="bg-transparent fs-xsmall">{{ $t('invoices.amount') }}</th>
									<th class="bg-transparent fs-xsmall">{{ $t('common.fields.status') }}</th>
									<th class="bg-transparent fs-xsmall">{{ $t('invoices.issuedAt') }}</th>
									<th class="bg-transparent fs-xsmall">{{ $t('common.fields.actions') }}</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="(invoice, index) in invoices" :key="index">
									<td>
										<div class="d-flex justify-content-start align-items-center">
											<div>
												<h6 class="mb-0 text-truncate" v-if="invoice.status === 'draft'">
													{{ $t('invoices.draftInvoice') }}
												</h6>
												<h6 class="mb-0 text-truncate" v-else>
													{{ $t('invoices.invoice', { number: invoice.number }) }}
												</h6>
												<p class="text-truncate mb-0">{{ formatRelativeDate(invoice.created) }}</p>
											</div>
										</div>
									</td>
									<td>
										{{ invoice.total / 100 }}€
									</td>
									<td>
										<span class="badge bg-label-success rounded-pill" v-if="invoice.status === 'paid'">
											{{ $t('invoices.paid') }}
										</span>
										<span class="badge bg-label-warning rounded-pill" v-else-if="invoice.status === 'draft'">
											{{ $t('invoices.draft') }}
										</span>
										<span class="badge bg-label-danger rounded-pill" v-else>
											{{ $t('invoices.pendingPayment') }}
										</span>
									</td>
									<td>
										{{ formatDate(invoice.created) }}
									</td>
									<td>
										<a :href="invoice.hosted_invoice_url" target="_blank" class="btn btn-sm btn-icon btn-text-secondary rounded-pill me-1 waves-effect waves-light" v-if="invoice.origin === 'stripe'">
											<i class="ri-eye-line ri-22px"></i>
										</a>
										<a :href="invoice.invoice_pdf" target="_blank" class="btn btn-sm btn-icon btn-text-secondary rounded-pill me-1 waves-effect waves-light">
											<i class="ri-download-2-line ri-22px"></i>
										</a>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</div>
		<!-- / Content -->

		<div class="content-backdrop fade"></div>
	</div>
</template>

<script>
import HTTP from "@/axios";
import moment from "moment";
import "moment/locale/fr";

moment.locale(localStorage.getItem('locale') || 'fr');

export default {
	name: "InvoicesPages",

	data() {
		return {
			isLoading: true,
			isDownloading: false,
			companyName: null,
			invoices: [],

			// null si la source a répondu, sinon { code, message } renvoyé par l'API
			pennylaneError: null,
			stripeError: null,
		};
	},

	computed: {
		sourceErrors() {
			const errors = [this.pennylaneError, this.stripeError].filter(Boolean);

			// Le message est volontairement le même quelle que soit la source : un seul bandeau si les deux tombent.
			return errors.filter((error, index) => errors.findIndex(other => other.message === error.message) === index);
		},
	},

	methods: {
		async fetchInvoices() {
			try {
				const response = await HTTP('/invoices');
				this.invoices = response.data.invoices;
				this.companyName = response.data.companyName;

				// L'API répond 200 même si une source de factures est tombée : la liste peut être incomplète.
				this.pennylaneError = response.data.pennylaneError || null;
				this.stripeError = response.data.stripeError || null;
			} catch (error) {
				// Récupérer le message d'erreur de l'API ou définir un message par défaut
				const errorMessage = error?.response?.data?.message || this.$t('errors.generic');

				alert(errorMessage);

				// Log détaillé dans la console (utile en développement)
				console.error("Erreur API:", error);
			} finally {
				this.isLoading = false;
			}
		},

		async downloadAllInvoices() {
			try {
				this.isDownloading = true;

				const { data } = await HTTP.post("/invoices/download", {}, { responseType: "blob" });

				const url = URL.createObjectURL(new Blob([data], { type: "application/zip" }));
				const a = document.createElement("a");
				a.href = url;
				a.download = `Factures-${this.companyName}.zip`;
				a.click();
				URL.revokeObjectURL(url);
			} catch (e) {
				alert(this.$t('errors.generic'));
			} finally {
				this.isDownloading = false;
			}
		},

		formatDate(timestamp) {
			return moment.unix(timestamp).format("DD/MM/YYYY"); // Affiche "il y a 3 semaines"
		},

		formatRelativeDate(timestamp) {
			return moment.unix(timestamp).fromNow(); // Affiche "il y a 3 semaines"
		},
	},

	async mounted() {
		this.fetchInvoices();
	}
};
</script>

<style scoped>
/* Ajoutez des styles spécifiques à la page Dashboard */
.dashboard {
	padding: 1rem;
}

.card-title {
	font-size: 1.2rem;
}

.card-text {
	margin: 0;
}

.load-center {
	display: block;
	margin: 0 auto;
}
</style>
